"use client";
import React from "react";
import { motion } from "motion/react";
import Link from "next/link";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowUpRight01Icon } from "@hugeicons/core-free-icons";
import { ProjectType } from "@/components/utils/types";
import { InfiniteMovingCards } from "@/components/ui/infinite-moving-cards";
import { MobileScreenCards } from "@/components/ProjectDetails/MobileScreenCards";

interface AdaptabilityProps {
  project: ProjectType;
}

export const Adaptability = ({ project }: AdaptabilityProps) => {
  return (
    <section className="relative w-full py-24 md:py-40 px-6 md:px-20 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-purple-500/5 dark:bg-purple-500/5 rounded-full blur-3xl -translate-x-1/2" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-sky-500/5 dark:bg-sky-500/5 rounded-full blur-3xl translate-x-1/2" />
      </div>

      <div className="px-2 md:px-20 lg:px-32 mx-auto relative z-10 flex flex-col gap-32 md:gap-40">
        {/* Desktop Friendliness */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Order change for mobile/desktop */}
          <div className="order-2 lg:order-1 lg:col-span-7 relative">
            <div className="absolute inset-0 bg-linear-to-tr from-sky-500/20 to-blue-500/20 rounded-full blur-3xl opacity-20 -z-10" />
            <InfiniteMovingCards
              speed="slow"
              direction="right"
              items={project.desktopScreenshots || []}
            />
          </div>

          <div className="lg:col-span-1 hidden lg:block order-1" />

          <div className="order-1 lg:order-2 lg:col-span-4 flex flex-col items-start text-left">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
                delay: 0.1,
              }}
              className="font-recoleta text-4xl md:text-4xl lg:text-6xl text-black dark:text-white mb-6 leading-tight"
            >
              Immersive{" "}
              <span className="bg-linear-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
                Workspace
              </span>
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
                delay: 0.2,
              }}
              className="font-outfit text-lg md:text-xl text-black/60 dark:text-white/60 leading-relaxed mb-10"
            >
              Experience the full power of {project.name} on the desktop. With
              an expansive canvas for complex project planning, drag-and-drop
              organization, and detailed analytics.
            </motion.p>

            <div className="flex flex-wrap gap-4">
              <Link
                href={project.url}
                target="_blank"
              >
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                    delay: 0.3,
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 15,
                    },
                  }}
                  whileTap={{
                    scale: 0.95,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 15,
                    },
                  }}
                  className="group flex items-center gap-2 px-8 py-4 bg-black dark:bg-white text-white dark:text-black font-medium font-outfit rounded-full text-base md:text-lg tracking-wide shadow-lg hover:shadow-sky-500/20 transition-all"
                >
                  <span>Launch App</span>
                  <HugeiconsIcon icon={ArrowUpRight01Icon} className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </motion.button>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Friendliness */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-5 flex flex-col items-start text-left">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
                delay: 0.1,
              }}
              className="font-recoleta text-4xl md:text-4xl lg:text-6xl text-black dark:text-white mb-6 leading-tight"
            >
              Fast &{" "}
              <span className="bg-linear-to-r from-blue-500 to-sky-500 bg-clip-text text-transparent">
                Responsive
              </span>
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                stiffness: 100,
                damping: 20,
                delay: 0.2,
              }}
              className="font-outfit text-lg md:text-xl text-black/60 dark:text-white/60 leading-relaxed mb-10"
            >
              Optimized for every device. We prioritize lightweight code and
              captivating animations to ensure user retention without
              sacrificing performance.
            </motion.p>

            <div className="flex flex-wrap gap-4">
              <Link
                href={project.url}
                target="_blank"
              >
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 100,
                    damping: 20,
                    delay: 0.3,
                  }}
                  whileHover={{
                    scale: 1.05,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 15,
                    },
                  }}
                  whileTap={{
                    scale: 0.95,
                    transition: {
                      type: "spring",
                      stiffness: 400,
                      damping: 15,
                    },
                  }}
                  className="group flex items-center gap-2 px-8 py-4 bg-black dark:bg-white text-white dark:text-black font-medium font-outfit rounded-full text-base md:text-lg tracking-wide shadow-lg hover:shadow-purple-500/20 transition-all"
                >
                  <span>Visit Live Site</span>
                  <HugeiconsIcon icon={ArrowUpRight01Icon} className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </motion.button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-1 hidden lg:block" />

          <div className="lg:col-span-6 relative">
            <div className="absolute inset-0 bg-linear-to-tr from-purple-500/20 to-pink-500/20 rounded-full blur-3xl opacity-20 -z-10" />
            <MobileScreenCards
              speed="slow"
              items={project.mobileScreenshots || []}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
